
import React, { useState } from 'react';
import Button from '../../../components/base/Button';

export default function NewsletterSection() {
  const [activeTab, setActiveTab] = useState('newsletters');

  const newsletters = [
    {
      title: 'Oxygen Health Weekly',
      date: 'December 2024',
      preview: 'Winter breathing tips and indoor air quality management strategies for better oxygen health.',
      image: 'https://static.readdy.ai/image/9842b5143901a0019f067b22648287f2/67c219b35f530b4d32cea9fc4e3f63eb.jfif',
      readTime: '5 min read',
      category: 'Health Tips'
    },
    {
      title: 'Community Impact Report',
      date: 'November 2024',
      preview: 'Highlighting success stories from our rural healthcare initiatives and oxygen awareness programs.',
      image: 'https://static.readdy.ai/image/9842b5143901a0019f067b22648287f2/a0b1df4b393ed7f2da11d694bbee5abe.jfif',
      readTime: '8 min read',
      category: 'Impact'
    },
    {
      title: 'Research Updates',
      date: 'October 2024', 
      preview: 'Latest scientific findings on oxygen deficiency prevention and new therapeutic approaches.',
      image: 'https://public.readdy.ai/ai/img_res/27b69609edf1ae2886fab61a59646cfb.jpg',
      readTime: '12 min read',
      category: 'Research'
    }
  ];

  const reels = [
    {
      title: 'What is Ox66®- powdered Oxygen',
      duration: '60s',
      views: '45K',
      thumbnail: 'https://static.readdy.ai/image/9842b5143901a0019f067b22648287f2/944108d27da4ab95fbb249ff717403c4.jfif',
      category: 'Tutorial'
    },
    {
      title: 'Oxygen Deficiency Signs',
      duration: '90s',
      views: '78K',
      thumbnail: 'https://readdy.ai/api/search-image?query=Educational%20video%20thumbnail%20about%20oxygen%20deficiency%20warning%20signs%20featuring%20medical%20professional%20explaining%20symptoms%2C%20healthcare%20awareness%20video%20content%20with%20clean%20medical%20graphics%20and%20teal%20color%20scheme%20for%20educational%20purposes&width=400&height=225&seq=reel-deficiency-v2&orientation=landscape',
      category: 'Awareness'
    },
    {
      title: 'Village Workshop Highlights',
      duration: '120s',
      views: '32K',
      thumbnail: 'https://readdy.ai/api/search-image?query=Community%20workshop%20video%20thumbnail%20in%20rural%20village%20setting%20with%20healthcare%20workers%20teaching%20oxygen%20awareness%20to%20diverse%20group%20of%20villagers%2C%20educational%20outreach%20video%20with%20warm%20natural%20lighting%20and%20engaging%20learning%20atmosphere&width=400&height=225&seq=reel-workshop-v2&orientation=landscape',
      category: 'Community'
    },
    {
      title: 'Quick Health Check Tips',
      duration: '45s',
      views: '56K',
      thumbnail: 'https://readdy.ai/api/search-image?query=Quick%20health%20check%20video%20thumbnail%20with%20medical%20professional%20demonstrating%20self-assessment%20techniques%20for%20oxygen%20levels%2C%20educational%20healthcare%20video%20content%20with%20clean%20medical%20setting%20and%20professional%20instructional%20style&width=400&height=225&seq=reel-healthcheck-v2&orientation=landscape',
      category: 'Tips'
    }
  ];

  return (
    <section className="py-20 bg-white" data-newsletter-section>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center max-w-4xl mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-[#111111] mb-6">
            Newsletters & Social Media Reels
          </h2>
          <p className="text-xl text-gray-700 leading-relaxed">
            Stay informed with our regular updates, health tips, and engaging video content 
            that makes oxygen health knowledge accessible and actionable.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex justify-center mb-12">
          <div className="bg-[#FAFAFA] p-1 rounded-full flex">
            <button
              onClick={() => setActiveTab('newsletters')}
              className={`px-8 py-3 rounded-full font-medium transition-all duration-200 whitespace-nowrap ${
                activeTab === 'newsletters'
                  ? 'bg-[#12B6B6] text-white shadow-lg'
                  : 'text-gray-600 hover:text-[#12B6B6]'
              }`}
            >
              Newsletters
            </button>
            <button
              onClick={() => setActiveTab('reels')}
              className={`px-8 py-3 rounded-full font-medium transition-all duration-200 whitespace-nowrap ${
                activeTab === 'reels'
                  ? 'bg-[#12B6B6] text-white shadow-lg'
                  : 'text-gray-600 hover:text-[#12B6B6]'
              }`}
            >
              Social Media Reels
            </button>
          </div>
        </div>

        {/* Newsletters Content */}
        {activeTab === 'newsletters' && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {newsletters.map((newsletter, index) => (
                <div 
                  key={index}
                  className="group bg-[#FAFAFA] rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img 
                      src={newsletter.image} 
                      alt={newsletter.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-medium text-[#12B6B6] bg-[#12B6B6]/10 px-2 py-1 rounded-full">
                        {newsletter.category}
                      </span>
                      <span className="text-xs text-gray-500">{newsletter.readTime}</span>
                    </div>
                    <h3 className="text-xl font-bold text-[#111111] mb-2">{newsletter.title}</h3>
                    <p className="text-sm text-gray-500 mb-3">{newsletter.date}</p>
                    <p className="text-gray-600 mb-4 leading-relaxed">{newsletter.preview}</p>
                    <Button variant="outline" size="sm" className="w-full">
                      Read Newsletter
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Newsletter Subscription */}
            <div className="bg-gradient-to-r from-[#12B6B6] to-[#0ea5a5] rounded-2xl p-8 text-white text-center">
              <h3 className="text-2xl font-bold mb-4">Stay Updated</h3>
              <p className="text-lg mb-6 opacity-90">
                Get weekly insights and health tips delivered to your inbox
              </p>
              <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                <input 
                  type="email" 
                  placeholder="Enter your email"
                  className="flex-1 px-4 py-3 rounded-xl text-gray-900 placeholder-gray-500"
                />
                <Button variant="secondary" size="md" className="whitespace-nowrap">
                  Subscribe Now
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Reels Content */}
        {activeTab === 'reels' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {reels.map((reel, index) => (
              <div 
                key={index}
                className="group bg-[#FAFAFA] rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer"
                onClick={() => {
                  if (index === 0) {
                    window.open('https://youtu.be/sF6kPCjQl5I?si=Xks7JOXroDeKXKau', '_blank');
                  }
                }}
              >
                <div className="aspect-[9/16] relative overflow-hidden">
                  <img 
                    src={reel.thumbnail} 
                    alt={reel.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center">
                      <i className="ri-play-fill text-2xl text-[#12B6B6]"></i>
                    </div>
                  </div>
                  <div className="absolute top-3 left-3">
                    <span className="text-xs font-medium text-white bg-[#12B6B6] px-2 py-1 rounded-full">
                      {reel.category}
                    </span>
                  </div>
                  <div className="absolute bottom-3 right-3 text-white text-xs bg-black/50 px-2 py-1 rounded">
                    {reel.duration}
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-[#111111] mb-2">{reel.title}</h3>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>{reel.views} views</span>
                    <span>{index === 0 ? 'Watch on YouTube' : 'Watch on social'}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
